/* eslint-disable */
import { AccountProfile, PermissionsKeys, UserAccountsCursorQueryParams, UserAccountsCursorResponse } from '@lynx/core/src/interfaces'
import { MoreDownloadsState } from '@lynx/core/src/interfaces/MoreDownloadsTable.types'
import { OrganisationMoveData, Region, RegionState } from '@lynx/core/src/interfaces/Region'
import { getMockedRegionToken, getRegionTokenSuffix } from '@lynx/core/src/constants/region'
import { UserProfile } from '@lynx/core/src/interfaces/User'
import { AxiosResponse } from 'axios'
import { Logger } from '../../modules/Logger'
import { BaseCall } from '../BaseCall'
import { APIConfig, APIResponse, CheckReturn, LoginProfile, ProfileReturn } from '../interfaces'
import LoginError from '../LoginError'
import { RegistrationResponseJSON, StartAuthenticationOpts, StartRegistrationOpts } from '@simplewebauthn/browser'

export class User extends BaseCall {
	public async webauthnLoginOptions(): Promise<{ challengeId: string; options: StartAuthenticationOpts }> {
		const response = await this.client.post<{ challengeId: string; options: StartAuthenticationOpts }>(
			'/auth/webauthn/login/options',
			{},
			{ withCredentials: true }
		)
		return response.data
	}

	public async webauthnLoginVerify(payload: { challengeId: string; credentialResponse: unknown }): Promise<unknown> {
		const response = await this.client.post(
			'/auth/webauthn/login/verify',
			{
				challengeId: payload.challengeId,
				credentialResponse: payload.credentialResponse
			},
			{
				withCredentials: true
			}
		)
		return response.data
	}

	public async webauthnRegisterOptions(): Promise<{ challengeId: string; options: StartRegistrationOpts }> {
		const response = await this.client.post<{ challengeId: string; options: StartRegistrationOpts }>('/auth/webauthn/register/options', null, {
			withCredentials: true
		})
		return response.data
	}

	public async webauthnListCredentials(): Promise<{
		credentials: Array<{
			credentialId: string
			label: string | null
			createdAt: string
			updatedAt: string | null
		}>
	}> {
		const response = await this.client.get<{
			credentials: Array<{
				credentialId: string
				label: string | null
				createdAt: string
				updatedAt: string | null
			}>
		}>('/auth/webauthn/credentials', { withCredentials: true })
		return response.data
	}

	public async webauthnDeleteCredential(credentialId: string): Promise<{ success: boolean }> {
		const encodedCredentialId = encodeURIComponent(credentialId)
		const response = await this.client.delete<{ success: boolean }>(`/auth/webauthn/credentials/${encodedCredentialId}`, { withCredentials: true })
		return response.data
	}

	public async webauthnUpdateCredentialLabel(payload: { credentialId: string; label: string | null }): Promise<{ success: boolean }> {
		const encodedCredentialId = encodeURIComponent(payload.credentialId)
		const response = await this.client.patch<{ success: boolean }>(
			`/auth/webauthn/credentials/${encodedCredentialId}`,
			{ label: payload.label },
			{ withCredentials: true }
		)
		return response.data
	}

	public async webauthnRegisterVerify(payload: {
		challengeId: string
		credentialResponse: RegistrationResponseJSON
		label?: string
	}): Promise<{ verified: boolean; credentialId: string }> {
		const response = await this.client.post<{ verified: boolean; credentialId: string }>(
			'/auth/webauthn/register/verify',
			{
				challengeId: payload.challengeId,
				credentialResponse: payload.credentialResponse,
				label: payload.label
			},
			{
				withCredentials: true
			}
		)
		return response.data
	}

	// Failures propagate so callers can keep the UI in sync with the actual backend state.
	public revokeUserPermission = async (userId: string, permissionKey: PermissionsKeys): Promise<void> => {
		await this.client.delete(`/user/userPermission/${userId}/${encodeURIComponent(permissionKey)}`)
	}
	public grantUserPermission = async (userId: string, permissionKey: PermissionsKeys): Promise<void> => {
		await this.client.post(`/user/userPermission/${userId}/${encodeURIComponent(permissionKey)}`)
	}
	public acceptLatestTerms = async (): Promise<boolean> => {
		try {
			const response = await this.client.post(
				'/user/acceptTerms',
				{},
				{
					withCredentials: true
				}
			)
			return response.status === 200
		} catch (e) {
			Logger.error(e)
			return false
		}
	}

	public NextCloudPoll = async (token: string, endpoint: string, organisationId?: number): Promise<APIResponse | { error: boolean }> => {
		const bodyFormData = new FormData()
		bodyFormData.set('token', token)
		bodyFormData.set('endpoint', endpoint)
		if (organisationId) bodyFormData.set('organisationId', organisationId.toString())

		try {
			const response = await this.client.post<APIResponse>('/auth/nextcloud/poll', bodyFormData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				},
				withCredentials: true
			})

			if (response.status === 200) {
				return response.data
			}

			throw new LoginError({
				data: 'Incorrect username or password'
			})
		} catch (err) {
			Logger.error(err)
			return { error: true }
		}
	}

	public check = async (username: string): Promise<APIResponse<CheckReturn>> => {
		const bodyFormData = new FormData()
		bodyFormData.set('username', username)
		//Not required on the check endpoint, but local auth requires it anyway
		bodyFormData.set('password', 'password')

		try {
			const response = await this.client.post<APIResponse<CheckReturn>>('/auth/login/check', bodyFormData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				},
				withCredentials: true
			})

			if (response.status === 200) {
				return response.data
			}

			throw new LoginError({
				data: 'Incorrect username or password'
			})
		} catch (err) {
			Logger.error(err)
			throw err
		}
	}

	public login = async (username: string, password: string, code?: string, scheme?: string): Promise<LoginProfile> => {
		const bodyFormData = new FormData()
		bodyFormData.set('username', username)
		bodyFormData.set('password', password)

		if (code) {
			bodyFormData.set('code', code)
		}

		scheme && bodyFormData.set('scheme', scheme)

		try {
			const response = await this.client.post(`/auth/login/token`, bodyFormData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				},
				withCredentials: true
			})

			if (response.status === 200) {
				return response.data
			}

			throw new LoginError({
				data: 'Incorrect username or password'
			})
		} catch (err: any) {
			if (err.response?.data?.redirect) {
				window.location = err.response.data.redirect
			}
			throw err
		}
	}

	public profile = async (): Promise<UserProfile> => {
		try {
			const response = await this.client.get<UserProfile>('/user')

			if (response.status === 200) {
				return response.data
			}

			throw new LoginError({
				data: 'Bad Profile'
			})
		} catch (e) {
			Logger.error(e)
			throw e
		}
	}

	public qrLoginSuccess = async (code: string): Promise<APIResponse> => {
		const response = await this.client.post(`/auth/qr/redirect/${code}`, {}, { withCredentials: true })
		return response.data
	}

	private renewWithCookie = async (): Promise<{ accessToken?: string }> => {
		const response = await this.client.post<{ access_token: string; refresh_token: string }>(
			'/auth/login/token/refresh',
			{},
			{
				withCredentials: true
			}
		)
		return {
			accessToken: response.data.access_token
		}
	}

	public renew = async (tokens: { accessToken: string; refreshToken: string; userId: string } | null = null): Promise<{ accessToken?: string }> => {
		try {
			if (!tokens) return this.renewWithCookie()

			const body = { refresh_token: tokens.refreshToken, user_id: tokens.userId }
			const response = await this.client.post<{ access_token: string; refresh_token: string }>('/auth/login/token/refresh', body, {
				withCredentials: true,
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${tokens.accessToken}`
				}
			})

			if (response.status !== 200 || !response.data) {
				Logger.error(response.statusText)
				return {}
			}

			return { accessToken: response.data.access_token }
		} catch (err) {
			Logger.error(err)
			return {}
		}
	}

	public getTokensFromTokenId = async (tokenId: string): Promise<{ accessToken: string }> => {
		const response = await this.client.post(
			'/user/token-id/tokens',
			{ tokenId },
			{
				headers: {
					'Content-Type': 'application/json'
				},
				withCredentials: true
			}
		)

		return response.data
	}

	public requestDelete = async (): Promise<void> => {
		await this.client.post('/user/requestDelete')
	}
	public getLynxVersions = async (): Promise<MoreDownloadsState['otherOutput']> => {
		const response = await this.client.get('/user/get-lynx-versions')
		return response.data
	}

	public forgottenPassword = async (username: string): Promise<any> => {
		const body = {
			userName: username
		}

		try {
			return await this.client.post('/auth/forgot', body, {
				headers: {
					'Content-Type': 'application/json'
				},
				withCredentials: true
			})
		} catch (err) {
			Logger.error(err)
			throw err
		}
	}

	public federatedCheck = async (code: string, { isWhiteboard }: { isWhiteboard: boolean }): Promise<{ error: boolean }> => {
		let url = '/auth/login/federated'
		if (isWhiteboard) url += '?deviceType=whiteboard'

		try {
			return await this.client.post(
				url,
				{ code },
				{
					headers: {
						'Content-Type': 'application/json'
					},
					withCredentials: true
				}
			)
		} catch (err) {
			Logger.error(err)
			return {
				error: true
			}
		}
	}

	public updateUserStorageLimit = async (userId: string, storageLimit: number | null, previousStorageLimit: number | null): Promise<boolean> => {
		try {
			const response = await this.client.post(`/user/storageLimit/${userId}`, { storageLimit, previousStorageLimit })

			return response.status === 200
		} catch (e) {
			Logger.error(e)
			throw e
		}
	}

	public addProSubscription = async (userId: string): Promise<boolean> => {
		try {
			const response = await this.client.post(`/user/pro/${userId}`)
			return response.status === 200
		} catch (e) {
			Logger.error(e)
			return false
		}
	}

	public removeProSubscription = async (userId: string): Promise<boolean> => {
		try {
			const response = await this.client.delete(`/user/pro/${userId}`)
			return response.status === 200
		} catch (e) {
			Logger.error(e)
			return false
		}
	}

	public validateEmailConfirmId = async (emailConfirmId: string): Promise<{ isValid: boolean }> => {
		try {
			const response = await this.client.get(`/auth/forgot-confirm/is-valid?emailConfirmId=${emailConfirmId}`)
			return response.data
		} catch (err) {
			Logger.error(err)
			throw err
		}
	}

	public resetPassword = async (user: string, id: string, password: string, emailConfirmId: string): Promise<any> => {
		const bodyFormData = new FormData()
		bodyFormData.set('userName', user)
		bodyFormData.set('resetId', id)
		bodyFormData.set('password', password)

		try {
			return await this.client.post('/auth/forgot-confirm', bodyFormData, {
				headers: {
					'Content-Type': 'multipart/form-data'
				},
				withCredentials: true
			})
		} catch (err) {
			Logger.error(err)
			throw err
		}
	}

	public changePassword = async (currentPassword: string, newPassword: string, repeatPassword: string): Promise<ProfileReturn> => {
		try {
			const response = await this.client.post('/profile/change-password', {
				currentPassword,
				newPassword,
				repeatPassword
			})
			return response.data
		} catch (err) {
			Logger.error(err)
			return {
				success: false,
				message: 'Something went wrong, could not continue!'
			}
		}
	}

	public changeEmail = async (newEmail: string): Promise<ProfileReturn> => {
		const response = await this.client.post('/profile/change-email', {
			newEmail
		})
		return response.data
	}

	public changeRegionAdmin = async (
		region: Region,
		adminOptions: { targetUserId: string; currentUserRegion: Region }
	): Promise<{ regionChangeId: string }> => {
		const qParams = `?token=${getMockedRegionToken(getRegionTokenSuffix(adminOptions.currentUserRegion))}`

		const response = await this.client.post(`/profile/change-region-admin${qParams}`, {
			region,
			targetUserId: adminOptions.targetUserId
		})
		return response.data
	}

	public deleteUserFromRegionAdmin = async (targetUserId: string, region: Region): Promise<void> => {
		const qParams = `?token=mockedToken@${region}`
		await this.client.post(`/profile/delete-user-from-region-admin${qParams}`, { targetUserId, region })
	}

	public getUserOrganisationChangeRegionAdmin = async (organisationId: number | null, userId: string | null): Promise<OrganisationMoveData> => {
		const qParams = organisationId ? `?organisationId=${organisationId}` : userId ? `?targetUserId=${userId}` : ''
		const response = await this.client.get<OrganisationMoveData>(`/profile/change-region-user-organisation-admin${qParams}`)
		return response.data
	}

	public getRegionState = async (): Promise<RegionState> => {
		const response = await this.client.get<RegionState>('/profile/region-state')
		return response.data
	}

	public updateRegionState = async (state: { isEnabled: boolean }): Promise<RegionState> => {
		const response = await this.client.post<RegionState>('/profile/region-state', state)
		return response.data
	}

	public confirmEmailChange = async (emailChangeId: string): Promise<{ newEmail: string }> => {
		const response = await this.client.post(`/profile/change-email/${emailChangeId}/confirm`)
		return response.data
	}

	public changeDisplayName = async (displayName: string): Promise<ProfileReturn> => {
		try {
			const response = await this.client.post('/profile/change-displayname', {
				displayName
			})

			if (response.status === 200) {
				return response.data
			}

			return {
				success: false,
				message: 'Something went wrong, could not continue!'
			}
		} catch (err) {
			Logger.error(err)
			return {
				success: false,
				message: 'Something went wrong, could not continue!'
			}
		}
	}

	public changeLang = async (lang: string): Promise<ProfileReturn> => {
		try {
			const response = await this.client.post('/profile/change-lang', {
				lang
			})

			if (response.status === 200) {
				return response.data
			}

			return {
				success: false,
				message: 'Something went wrong, could not continue!'
			}
		} catch (err) {
			Logger.error(err)
			return {
				success: false,
				message: 'Something went wrong, could not continue!'
			}
		}
	}

	public checkEmail = async (email: string): Promise<{ error: boolean | string }> => {
		try {
			const response = await this.client.post(`/check-email`, { email })
			return response.data
		} catch (err) {
			Logger.error(err)
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			return err.response.data
		}
	}

	public getAll = async (
		body: UserAccountsCursorQueryParams & { isCrossRegion?: boolean }
	): Promise<{
		data: UserAccountsCursorResponse<AccountProfile>
	} | null> => {
		try {
			const response = await this.client.post('/user/all', body)
			return response.data
		} catch (e) {
			Logger.error(e)
			return null
		}
	}

	public getLynxKeys = async (): Promise<ProfileReturn> => {
		try {
			const response = await this.client.get('/profile/get-lynx-keys')
			return response.data
		} catch (err) {
			Logger.error(err)
			return {
				success: false,
				message: 'Something went wrong, could not continue!'
			}
		}
	}

	public setProfilePicture = async (pictureFile: File): Promise<{ url: string }> => {
		try {
			const formData = new FormData()
			formData.append('file', pictureFile)

			const response = await this.client.post('/profile/upload-avatar', formData)
			return response.data
		} catch (err) {
			Logger.error(err)
			throw err
		}
	}

	public getProviderAvatars = async (): Promise<any> => {
		try {
			const response = await this.client.get('/profile/providers-avatars')
			return response.data
		} catch (err) {
			Logger.error(err)
		}
	}

	public resendVerificationEmail = async (username: string, password: string): Promise<AxiosResponse> => {
		const body = {
			email: username,
			password: password
		}

		return await this.client.post('/auth/resend-verification', body, {
			headers: {
				'Content-Type': 'application/json'
			}
		})
	}

	public register = (username: string, password: string): Promise<AxiosResponse> => {
		const body = {
			userName: username,
			password: password
		}

		return this.client.post('/auth/register', body, {
			headers: {
				'Content-Type': 'application/json'
			}
		})
	}

	public async createSession(
		deviceType: 'whiteboard' | null = null,
		{ withCredentials, isQrCodeLogin = false }: { withCredentials: boolean; isQrCodeLogin?: boolean }
	): Promise<{ accessToken: string; refreshToken: string; expireAt: string; sessionId: string }> {
		const url = new URL('http://dummy.com')
		deviceType && url.searchParams.append('deviceType', deviceType)
		isQrCodeLogin && url.searchParams.append('isQrCodeLogin', 'true')

		const response = await this.client.get('/auth/create-session?' + url.searchParams.toString(), {
			withCredentials
		})
		return response.data
	}

	public async logout(): Promise<AxiosResponse> {
		const response = await this.client.get('/auth/logout', {
			withCredentials: true
		})
		return response
	}

	public async logoutEverywhere(): Promise<AxiosResponse> {
		const response = await this.client.get('/auth/logout/everywhere', {
			withCredentials: true
		})
		return response
	}

	public async linkOAuthFlowToQrCode(qrCode: string): Promise<void> {
		await this.client.post('/auth/oauth/qr-flow-link', { qrCode }, { withCredentials: true })
	}

	public async getOAuthConsent(qrCode?: string): Promise<{
		clientId: string
		clientName: string
		description: string | null
		iconUrl: string | null
		scope: string | null
		redirectUri: string
		state: string | null
		consentGranted: boolean
	}> {
		const response = await this.client.get('/auth/oauth/consent', {
			withCredentials: true,
			params: qrCode ? { qrCode } : undefined
		})
		return response.data
	}

	public async acceptOAuthConsent(qrCode?: string): Promise<void> {
		await this.client.post('/auth/oauth/consent/accept', qrCode ? { qrCode } : null, { withCredentials: true })
	}

	public async getOAuthUserConsents(): Promise<{
		consents: {
			clientId: string
			clientName: string
			description: string | null
			iconUrl: string | null
			scope: string
			createdAt: string
		}[]
	}> {
		const response = await this.client.get('/auth/oauth/consents', { withCredentials: true })
		return response.data
	}

	public async revokeOAuthUserConsent(clientId: string): Promise<void> {
		await this.client.delete(`/auth/oauth/consents/${encodeURIComponent(clientId)}`, { withCredentials: true })
	}

	public setConfig(config: APIConfig): void {
		this.setBaseUrl(config.API_SERVER)
	}
}
